import { type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useAbaContext } from './aba-contexto';
import { cn } from '../ui/cn';

/**
 * PageHead — bloco de título padrão das páginas dentro do AppShell
 * (Design System v2 / [UI-F2] §page-head).
 *
 * Estrutura:
 *   - `<h1>` em font-display, text-2xl, font-extrabold, tracking-tight.
 *   - `<p>` subtítulo com text-tertiary, text-sm — opcional.
 *   - `actions` à direita (flex justify-between) — opcional, costuma ser
 *     1 ou 2 botões + meta-info ("atualizado às HH:MM").
 *
 * Usado por todas as páginas migradas para o v2 — substitui os títulos
 * inline que cada Admin* tinha. O respiro inferior padrão (`mb-6`) já vem
 * embutido; caller só envolve com Fragment.
 */

interface PageHeadProps {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

export function PageHead({ title, subtitle, actions, className }: PageHeadProps): JSX.Element | null {
  // Dentro de uma aba de `TelaComAbas`: título/subtítulo são da tela; só as ações da aba ATIVA sobem para o cabeçalho dela.
  const aba = useAbaContext();
  if (aba) {
    return aba.ativa && aba.slot && actions !== undefined ? createPortal(actions, aba.slot) : null;
  }
  return (
    <header
      className={cn(
        'mb-6 flex flex-wrap items-end justify-between gap-4',
        className,
      )}
    >
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-text-primary leading-tight">
          {title}
        </h1>
        {subtitle !== undefined && (
          <p className="mt-1 text-sm text-text-tertiary">{subtitle}</p>
        )}
      </div>
      {actions !== undefined && (
        <div className="flex items-center gap-2">{actions}</div>
      )}
    </header>
  );
}
